import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {TreatmentRequestSmall, TreatmentRequestTreatmentValidationSchema} from '@/features/treatment-requests/types'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {FormFieldBox} from '@/components/ui/form-atoms/FormAtoms'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {ObjectKeys} from '@/utilities/helpers'
import {treatmentRequestSectorToLabel, typeToLabel} from '@/features/treatment-requests/utils'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {TreatmentRequestAuthorizationForm} from '@/features/treatment-requests/components/treatment-request-authorization-form/TreatmentRequestAuthorizationForm'
import {TreatmentRequestProjectResumptionCard} from '@/features/treatment-requests/components/treatment-request-project-resumption-card/TreatmentRequestProjectResumptionCard'
import {useMemo} from 'react'
import {useParams} from 'react-router-dom'

export const TreatmentRequestTreatmentForm = ({
    patientId,
    treatmentRequestStatus
}: {
    patientId: string
    treatmentRequestStatus?: TreatmentRequestSmall['status']
}) => {
    const urlParams = useParams<{patientId: string}>()
    const {t} = useTranslation()

    // methods
    const form = useFormContext<TreatmentRequestTreatmentValidationSchema>()
    const type = useWatch({control: form.control, name: 'type'})

    const disabledSectorTypeFields = useMemo(
        () =>
            !!treatmentRequestStatus &&
            treatmentRequestStatus != 'waiting_list' &&
            treatmentRequestStatus != 'in_progress',
        [treatmentRequestStatus]
    )

    return (
        <Flexbox direction="column" gap={6}>
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>{t('commons:workSector')}</Label>
                <Controller
                    control={form.control}
                    name="sector"
                    render={({field, fieldState}) => (
                        <Flexbox fullWidth direction="column" gap={1.5}>
                            <Grid columns={3} columnGap={4} fullWidth>
                                {ObjectKeys(treatmentRequestSectorToLabel).map(sectorKey => (
                                    <FormFieldBox
                                        key={sectorKey}
                                        spanColumn={1}
                                        hasError={!!fieldState.error?.message}
                                        isSelected={field.value == sectorKey}
                                        isDisabled={disabledSectorTypeFields}
                                    >
                                        <RadioField
                                            label={t(treatmentRequestSectorToLabel[sectorKey])}
                                            checked={field.value == sectorKey}
                                            name={field.name}
                                            onChange={field.onChange}
                                            value={sectorKey}
                                            disabled={disabledSectorTypeFields}
                                        />
                                    </FormFieldBox>
                                ))}
                            </Grid>
                            {fieldState.error?.message && <InputHelpText error={fieldState.error.message} />}
                        </Flexbox>
                    )}
                />
            </Flexbox>

            <TreatmentRequestAuthorizationForm patientId={patientId} />

            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>{t('treatment_requests:request_type')}</Label>
                <Flexbox direction="column" gap={5} fullWidth>
                    <Controller
                        control={form.control}
                        name="type"
                        render={({field, fieldState}) => (
                            <Flexbox fullWidth direction="column" gap={1.5}>
                                <Grid columns={3} columnGap={4} fullWidth>
                                    {ObjectKeys(typeToLabel).map(typeKey => (
                                        <FormFieldBox
                                            key={typeKey}
                                            spanColumn={1}
                                            isSelected={field.value == typeKey}
                                            isDisabled={disabledSectorTypeFields || !!urlParams?.patientId}
                                            hasError={
                                                field.value == typeKey &&
                                                ((!!fieldState.error?.message && field.value == typeKey) ||
                                                    (!!fieldState.error?.message && fieldState.error.type != 'custom'))
                                            }
                                        >
                                            <Flexbox direction="column" gap={1}>
                                                <RadioField
                                                    label={t(typeToLabel[typeKey])}
                                                    checked={field.value == typeKey}
                                                    name={field.name}
                                                    onChange={value => {
                                                        field.onChange(value)
                                                        form.clearErrors('type')
                                                    }}
                                                    value={typeKey}
                                                    disabled={disabledSectorTypeFields || !!urlParams?.patientId}
                                                />
                                            </Flexbox>
                                        </FormFieldBox>
                                    ))}
                                </Grid>
                                {fieldState.error?.message && !type ? (
                                    <InputHelpText error={t('errors:required')} />
                                ) : (
                                    fieldState.error?.message && <InputHelpText error={fieldState.error?.message} />
                                )}
                            </Flexbox>
                        )}
                    />
                    {type && type != 'new_takeover' && (
                        <TreatmentRequestProjectResumptionCard patientId={patientId} type={type} />
                    )}
                </Flexbox>
            </Flexbox>
        </Flexbox>
    )
}
